/**
 * @file WarehouseLabelsPanel.tsx
 * @description Вкладка «Этикетки» настроек склада: принтер по умолчанию,
 *   режим печати, коды по умолчанию и три листа самоклейки с калибровкой
 *   (phase19.22, W17-5a/5b/5c; ui-design §3).
 *
 * Отдельная вкладка, а не часть «Устройств»: там железо, здесь бумага.
 * Принтер по умолчанию браузерную печать не направляет (браузер спрашивает
 * сам) — это подписано: «для журнала и будущего терморежима». На 1280 — две
 * колонки: слева принтер и режим, справа листы. Без права управления
 * складом — только просмотр.
 *
 * phase19.31 (зона «Склад»): «Сохранить» — главное действие в верхней панели
 * (на телефоне — в общей нижней полосе каркаса), своя липкая полоса убрана;
 * размеры кнопок и полей — из темы.
 */

import { Info, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { LabelSheetTemplateCard } from "./LabelSheetTemplateCard";
import { useWarehouseLabelSheets } from "./useWarehouseLabelSheets";
import { WmsSettingsHeader } from "./WmsSettingsHeader";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { SectionLabel } from "../../ui/SectionLabel";
import { SelectField } from "../../ui/SelectField";
import { LABEL_CODE_MODE_META } from "../../../lib/api-labels";
import { cn } from "@/lib/utils";

interface Props {
  warehouseId: string;
  /** Право `SHOP_WMS_MANAGE`: сохранять калибровку и принтер. */
  canManage: boolean;
  /** Перейти на вкладку «Устройства» — добавить принтер этикеток. */
  onGoToDevices: () => void;
  /** Строка «Склад ▾» — первой строкой (её рисует страница). */
  warehousePicker?: ReactNode;
}

/** Режимы печати — данными; термопринтер пока только в модели (W17-5a). */
const PRINT_MODES = [
  { key: "sheet", label: "Лист A4 — самоклейка", disabled: false },
  { key: "thermal", label: "Термопринтер — скоро", disabled: true },
] as const;

export function WarehouseLabelsPanel({ warehouseId, canManage, onGoToDevices, warehousePicker }: Props) {
  const form = useWarehouseLabelSheets(warehouseId);
  const header = (
    <WmsSettingsHeader
      action={
        canManage ? (
          <Button variant="brand" onClick={() => void form.save()} disabled={!form.valid || form.saving} loading={form.saving}>
            Сохранить
          </Button>
        ) : undefined
      }
    />
  );

  if (form.loading && !form.data) {
    return (
      <>
        {header}
        {warehousePicker}
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      </>
    );
  }
  if (form.error || !form.data) {
    return (
      <>
        <WmsSettingsHeader />
        {warehousePicker}
        <Alert severity="error">{form.error ?? "Не удалось загрузить настройки этикеток."}</Alert>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="max-w-[45rem]">{warehousePicker}</div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:gap-6">
        <div className="flex flex-col gap-3">
          <SectionLabel as="h2">Принтер этикеток</SectionLabel>
          {form.printers.length === 0 ? (
            <div className="flex flex-col items-start gap-2 rounded-xl bg-surface-low p-3 text-sm">
              <span>Принтер этикеток не добавлен. Печать работает и без него.</span>
              <Button variant="outline" onClick={onGoToDevices}>
                Добавить в «Устройства»
              </Button>
            </div>
          ) : (
            <SelectField
              value={form.printerId}
              onChange={form.setPrinterId}
              options={[{ value: "", label: "— не выбран —" }, ...form.printers.map((p) => ({ value: p.id, label: p.name }))]}
              ariaLabel="Принтер этикеток по умолчанию"
              disabled={!canManage}
            />
          )}
          <span className="type-caption font-medium text-on-surface-variant">Режим печати</span>
          <div className="flex flex-col gap-1.5">
            {PRINT_MODES.map((mode) => (
              <button
                key={mode.key}
                type="button"
                aria-pressed={mode.key === "sheet"}
                disabled={mode.disabled}
                className={cn(
                  "type-body min-h-[var(--control-h,3rem)] rounded-lg px-4 text-left",
                  mode.key === "sheet"
                    ? "bg-accent font-medium text-accent-foreground [box-shadow:inset_0_0_0_1px_color-mix(in_srgb,var(--primary)_35%,transparent)]"
                    : "bg-surface-low text-on-surface-variant",
                )}
              >
                {mode.label}
              </button>
            ))}
          </div>
          <p className="type-caption flex items-start gap-1.5 text-on-surface-variant">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            Браузер спросит принтер сам — выберите этот и масштаб 100 %. Принтер по умолчанию —
            для журнала печати и будущего терморежима.
          </p>
          <span className="type-caption font-medium text-on-surface-variant">Коды на этикетке по умолчанию</span>
          <p className="type-body">
            {LABEL_CODE_MODE_META[form.data.defaultCodeMode].label}
            <span className="type-caption block text-on-surface-variant">
              Выбирается при каждой печати; сохранение значения склада — в следующем релизе.
            </span>
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <SectionLabel as="h2">Листы самоклейки</SectionLabel>
          {form.data.sheets.map((sheet, index) => {
            const draft = form.drafts[sheet.template];
            return draft ? (
              <LabelSheetTemplateCard
                key={sheet.template}
                sheet={sheet}
                draft={draft}
                onChange={(next) => form.setDraft(sheet.template, next)}
                onCalibrate={() => form.calibrate(sheet.template)}
                defaultOpen={index === 0}
                readOnly={!canManage}
              />
            ) : null;
          })}
        </div>
      </div>

    </div>
  );
}
